<template>
  <div
    ref="modalEl"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="kvModalTitle"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-scrollable kv-modal">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="kvModalTitle">Kostenvoranschlag erstellen</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Schließen" :disabled="submitting"></button>
        </div>

        <div class="modal-body">
          <!-- Art der Beschädigung -->
          <div class="mb-3">
            <label class="form-label" for="kv-damage-type">Art der Beschädigung</label>
            <select id="kv-damage-type" v-model="templateId" class="form-select">
              <option :value="0">– bitte wählen –</option>
              <option v-for="t in templates" :key="t.id" :value="t.id">{{ t.description }}</option>
            </select>
          </div>

          <!-- Zu tauschende Ersatzteile -->
          <div class="mb-3">
            <label class="form-label">Zu tauschende Ersatzteile</label>
            <div class="kv-parts">
              <div class="form-check form-check-inline">
                <input id="kv-p-display" class="form-check-input" type="checkbox" v-model="parts.display" />
                <label class="form-check-label" for="kv-p-display">Display</label>
              </div>
              <div class="form-check form-check-inline">
                <input id="kv-p-backcover" class="form-check-input" type="checkbox" v-model="parts.backcover" />
                <label class="form-check-label" for="kv-p-backcover">Backcover</label>
              </div>
              <div class="form-check form-check-inline">
                <input id="kv-p-camera" class="form-check-input" type="checkbox" v-model="parts.camera" />
                <label class="form-check-label" for="kv-p-camera">Hauptkamera</label>
              </div>
              <div class="form-check form-check-inline">
                <input id="kv-p-swap" class="form-check-input" type="checkbox" v-model="parts.swap" />
                <label class="form-check-label" for="kv-p-swap">SWAP</label>
              </div>
              <div class="form-check form-check-inline">
                <input id="kv-p-board" class="form-check-input" type="checkbox" v-model="parts.board" />
                <label class="form-check-label" for="kv-p-board">Platine</label>
              </div>
            </div>
          </div>

          <!-- Displayschutzfolie -->
          <div class="mb-3 form-check">
            <input id="kv-cover" class="form-check-input" type="checkbox" v-model="hasProtectiveCover" />
            <label class="form-check-label" for="kv-cover">Displayschutzfolie vorhanden</label>
          </div>

          <div class="kv-diag-row">
            <!-- Schadensdiagnose -->
            <div class="kv-diag-main">
              <label class="form-label" for="kv-diag">Schadensdiagnose</label>
              <textarea id="kv-diag" v-model="errorDescription" class="form-control" rows="6"></textarea>
            </div>
            <!-- Kleinteile -->
            <div class="kv-diag-small">
              <label class="form-label" for="kv-small">Kleinteile</label>
              <select id="kv-small" v-model="selectedSmallparts" class="form-select" multiple size="6">
                <option v-for="s in SMALLPARTS" :key="s.text" :value="s.text">{{ s.description }}</option>
              </select>
            </div>
          </div>

          <!-- Versicherungstext (nur Assona) -->
          <div v-if="isAssona" class="mb-3">
            <label class="form-label" for="kv-insurance">Versicherungstext</label>
            <textarea id="kv-insurance" v-model="insuranceText" class="form-control" rows="3"></textarea>
          </div>

          <!-- iPhone Helligkeitsautomatik (nur Apple) -->
          <div v-if="isApple" class="mb-3 form-check">
            <input id="kv-iphone" class="form-check-input" type="checkbox" v-model="iphoneCheck" />
            <label class="form-check-label" for="kv-iphone">iPhone-Displayhinweis an die Schadensdiagnose anfügen</label>
          </div>

          <!-- Reparaturinformationen -->
          <div class="mb-3">
            <label class="form-label" for="kv-repair">Reparaturinformationen</label>
            <textarea id="kv-repair" v-model="repairInformation" class="form-control" rows="2"></textarea>
          </div>

          <!-- Schadensfoto -->
          <div class="mb-3">
            <label class="form-label" for="kv-photo">Schadensfoto</label>
            <select id="kv-photo" v-model="damagePictureFileId" class="form-select">
              <option :value="null">– keins –</option>
              <option v-for="f in files" :key="f.id" :value="f.id">{{ f.description }}</option>
            </select>
          </div>

          <!-- Bruttobetrag -->
          <div class="mb-2 kv-price-row">
            <div>
              <label class="form-label" for="kv-brutto">Bruttobetrag (€)</label>
              <input
                id="kv-brutto"
                v-model="brutto"
                type="number"
                min="0"
                step="0.01"
                class="form-control kv-brutto-input"
                placeholder="z. B. 100,00"
                :readonly="isComplaint"
              />
              <small v-if="isComplaint" class="text-muted">Reklamation — fest 0,00 €.</small>
            </div>
            <div class="kv-price-derived">
              <div>Netto: <strong>{{ nettoText }}</strong> €</div>
              <div>MwSt 20%: <strong>{{ vatText }}</strong> €</div>
            </div>
          </div>

          <div v-if="formError" class="error mt-2">{{ formError }}</div>
          <div v-if="submitError" class="error mt-2">{{ submitError }}</div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" :disabled="submitting">Abbrechen</button>
          <button type="button" class="btn btn-primary" :disabled="submitting" @click="onSubmit">
            {{ submitting ? 'Speichert…' : 'Erstellen' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, useTemplateRef, watch } from 'vue';
import { Modal } from 'bootstrap';
import {
  createCostEstimate,
  listKvTemplates,
  type CostEstimate,
  type KvTemplate,
} from '../services/costEstimates';
import type { TicketDetail, TicketFile } from '../services/tickets';

/**
 * Formular zum Anlegen eines Kostenvoranschlags. Aufruf via `open(ticket)`.
 * Das Promise löst mit dem erstellten KV auf (Erfolg) oder mit `null` (abgebrochen).
 *
 * Felder wie im Altsystem (php/reptool/kvcreationdialog.php), ohne „Servicepartner KV":
 * Die Schadensdiagnose wird aus Schadensart + angehakten Ersatzteilen + Kleinteilen
 * + Displayschutz-Hinweis automatisch zusammengesetzt (editierbar).
 */

// Kleinteile — hartkodiert wie im Alt-Dialog (drawSmallpartsSelection).
const SMALLPARTS: { description: string; text: string }[] = [
  { description: 'Rearcover',       text: '- am Rearcover' },
  { description: 'Ladebuchse',      text: '- an der Ladebuchse' },
  { description: 'Lautsprecher',    text: '- am Lautsprecher' },
  { description: 'Kopfhörerbuchse', text: '- an der Kopfhörerbuchse' },
  { description: 'Frontkamera',     text: '- an der Frontkamera' },
  { description: 'SIM Kartenleser', text: '- am Simkartenleser' },
  { description: 'SD Kartenleser',  text: '- am Speicherkartenleser' },
  { description: 'Receiver',        text: '- am Receiver' },
  { description: 'Mikrofon',        text: '- am Mikrofon' },
  { description: 'Ladeelektronik',  text: '- an der Ladeelektronik' },
  { description: 'Sensorik',        text: '- an der Sensorik' },
  { description: 'Power Key',       text: '- am Power Key' },
  { description: 'Side Key',        text: '- am Side Key' },
  { description: 'Home Key',        text: '- am Home Key' },
  { description: 'Akku',            text: '- am Akku' },
  { description: 'Antenne',         text: '- an der Antenne' },
  { description: 'Hörlautsprecher', text: '- am Hörlautsprecher' },
];

/**
 * Vorauswahl der Schadensart: „mech. Beschädigung" ist der Normalfall.
 * Schlüssel statt id, damit die Stammdaten (kv_templates) frei bleiben.
 */
const DEFAULT_DAMAGE_TYPE_KEY = 'MECHANICAL_DAMAGE';

// Texte der Ersatzteil-Checkboxen (Alt-Dialog drawChangingPartRow).
const PART_TEXT: Record<string, string> = {
  display:   '- am Display / Touch',
  backcover: '- am Backcover',
  camera:    '- an der Hauptkamera',
  board:     '- an der Platine',
};

const modalEl = useTemplateRef<HTMLDivElement>('modalEl');
let   modal: Modal | null = null;
let   resolver: ((result: CostEstimate | null) => void) | null = null;

const ticketId = ref<number>(0);
const isAssona = ref<boolean>(false);
const isApple  = ref<boolean>(false);
/** Reklamation: Betrag fest 0, der Server erzwingt das ebenfalls. */
const isComplaint = ref<boolean>(false);
const files    = ref<TicketFile[]>([]);

const templates = ref<KvTemplate[]>([]);

const templateId          = ref<number>(0);
const parts               = reactive({ display: false, backcover: false, camera: false, swap: false, board: false });
const hasProtectiveCover  = ref<boolean>(false);
const selectedSmallparts  = ref<string[]>([]);
const errorDescription    = ref<string>('');
const insuranceText       = ref<string>('');
const iphoneCheck         = ref<boolean>(false);
const repairInformation   = ref<string>('');
const damagePictureFileId = ref<number | null>(null);
const brutto              = ref<number | string>('');

const formError   = ref<string | null>(null);
const submitError = ref<string | null>(null);
const submitting  = ref<boolean>(false);

let result: CostEstimate | null = null;

const bruttoNum = computed(() => {
  // <input type="number"> liefert je nach Browser/Vue eine Zahl oder einen String.
  const raw = typeof brutto.value === 'number'
    ? brutto.value
    : Number.parseFloat(String(brutto.value ?? '').replace(',', '.'));
  return Number.isFinite(raw) && raw > 0 ? raw : 0;
});
const nettoNum = computed(() => Math.round((bruttoNum.value / 1.2) * 100) / 100);
const vatNum   = computed(() => Math.round((bruttoNum.value - nettoNum.value) * 100) / 100);
const nettoText = computed(() => fmt(nettoNum.value));
const vatText   = computed(() => fmt(vatNum.value));

function fmt(value: number): string {
  return value.toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Schadensdiagnose aus den Auswahlfeldern zusammensetzen (wie Alt-getDamageDescription). */
function compose(): string {
  const lines: string[] = [];
  const tpl = templates.value.find(t => t.id === templateId.value);
  if (tpl) {
    lines.push(tpl.text);
    if (tpl.additional_text) lines.push(tpl.additional_text);
  }
  if (parts.display)   lines.push(PART_TEXT.display);
  if (parts.backcover) lines.push(PART_TEXT.backcover);
  if (parts.camera)    lines.push(PART_TEXT.camera);
  if (parts.board)     lines.push(PART_TEXT.board);
  // SWAP hat bewusst keinen Text.
  for (const text of selectedSmallparts.value) lines.push(text);
  if (hasProtectiveCover.value) lines.push('Displayschutz vorhanden');
  if (iphoneCheck.value) {
    const warn = templates.value.find(t => t.key === 'IPHONE_11_WARNING');
    if (warn) lines.push(warn.text);
  }
  return lines.join('\n');
}

// Bei jeder Auswahländerung die Schadensdiagnose neu zusammensetzen (überschreibt
// den Textbereich — manuelle Edits gelten bis zur nächsten Auswahländerung, wie im Alt-Tool).
watch(
  [templateId, () => ({ ...parts }), hasProtectiveCover, selectedSmallparts, iphoneCheck],
  () => { errorDescription.value = compose(); },
  { deep: true },
);

onMounted(() => {
  if (modalEl.value) {
    modal = new Modal(modalEl.value, { backdrop: 'static', keyboard: false });
    modalEl.value.addEventListener('hidden.bs.modal', onHidden);
  }
});

onBeforeUnmount(() => {
  modalEl.value?.removeEventListener('hidden.bs.modal', onHidden);
  modal?.dispose();
  modal = null;
});

async function open(ticket: TicketDetail): Promise<CostEstimate | null> {
  ticketId.value = ticket.id;
  isAssona.value = ticket.type === 'Assona24H';
  isApple.value  = /iphone|apple/i.test(ticket.device?.description ?? ticket.alternative_device_description ?? '');
  isComplaint.value = ticket.is_complaint;
  files.value    = ticket.files ?? [];

  parts.display = parts.backcover = parts.camera = parts.swap = parts.board = false;
  hasProtectiveCover.value  = false;
  selectedSmallparts.value  = [];
  errorDescription.value    = '';
  insuranceText.value       = '';
  iphoneCheck.value         = false;
  repairInformation.value   = '';
  damagePictureFileId.value = null;
  brutto.value              = ticket.is_complaint ? 0 : '';
  formError.value           = null;
  submitError.value         = null;
  submitting.value          = false;
  result                    = null;

  if (templates.value.length === 0) {
    try { templates.value = await listKvTemplates(); } catch { /* Dropdown bleibt leer */ }
  }

  // Nach dem Laden, nicht davor: die Vorlagen kommen erst hier an. Die Diagnose
  // wird direkt gesetzt und nicht dem Watcher überlassen — beim zweiten Öffnen
  // stünde derselbe Wert schon da und der Watcher würde nicht auslösen.
  templateId.value       = templates.value.find(t => t.key === DEFAULT_DAMAGE_TYPE_KEY)?.id ?? 0;
  errorDescription.value = compose();

  return new Promise<CostEstimate | null>(resolve => {
    resolver = resolve;
    modal?.show();
  });
}

async function onSubmit(): Promise<void> {
  if (submitting.value) return;
  formError.value = null;

  if (bruttoNum.value <= 0 && !isComplaint.value) {
    formError.value = 'Bitte einen Bruttobetrag größer 0 eingeben.';
    return;
  }
  if (errorDescription.value.trim() === '') {
    formError.value = 'Bitte eine Schadensdiagnose angeben.';
    return;
  }

  submitting.value  = true;
  submitError.value = null;
  try {
    result = await createCostEstimate(ticketId.value, {
      bru_price:              bruttoNum.value,
      error_description:      errorDescription.value.trim(),
      repair_info:            repairInformation.value.trim(),
      insurance_info:         isAssona.value ? insuranceText.value.trim() : undefined,
      damage_picture_file_id: damagePictureFileId.value,
    });
    modal?.hide();
  } catch (e) {
    submitError.value = e instanceof Error ? `Speichern fehlgeschlagen: ${e.message}` : 'Speichern fehlgeschlagen.';
    submitting.value  = false;
  }
}

function onHidden(): void {
  resolver?.(result);
  resolver = null;
}

defineExpose({ open });
</script>

<style scoped>
.kv-modal { max-width: 680px; }
/* Etwas breiter, dafür kleinere Texte und engere Abstände. */
.kv-modal :deep(.modal-title)   { font-size: 16px; }
.kv-modal :deep(.modal-body)    { padding: 0.75rem 1rem; }
.kv-modal :deep(.mb-3)          { margin-bottom: 0.55rem !important; }
.kv-modal :deep(.form-label)    { margin-bottom: 0.2rem; font-size: 12.5px; font-weight: 500; }
.kv-modal :deep(.form-control),
.kv-modal :deep(.form-select)   { font-size: 13px; padding: 0.25rem 0.5rem; }
.kv-modal :deep(.form-check-label) { font-size: 13px; }
.kv-parts {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 14px;
}
.kv-diag-row {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}
.kv-diag-main { flex: 1 1 auto; }
.kv-diag-small { flex: 0 0 170px; }
.kv-diag-small .form-select { height: 100%; }
.kv-price-row {
  display: flex;
  align-items: flex-end;
  gap: 24px;
}
.kv-brutto-input { max-width: 200px; }
.kv-price-derived { font-size: 14px; line-height: 1.6; }
</style>
